import readings from './oudekerkYardReadings.json';
import type { BuildContext } from '../../types';
import type { PolygonIndex } from '../../../geo/polygonIndex';
import type { Ring, Vec2 } from '../../../geo/types';
import { centroid, segmentIntersection } from '../../../geo/polygon';
import { closestPointOnPolyline } from '../../../geo/polyline';
import { LOT } from '../lotRules';
import { measureLine } from '../measuredLine';
import type { ReservedFootprint } from '../types';
import type { SurveyedRow } from './rowTypes';
import { surveyedRowLots } from './rowLots';

type Layout = BuildContext['layout'];

/** A row of trees beside a street: `count` of them from one chainage to the other, `inside` it. */
interface TreeRow {
  readonly id: string;
  readonly street: string;
  readonly from: number;
  readonly to: number;
  readonly inside: number;
  readonly count: number;
}

export const OUDEKERK_TOWER_ROWS = (readings.rows as readonly SurveyedRow[]).filter(
  (row) => row.id === 'oudekerk-warmoes-toren'
);
export const OUDEKERK_PLEIN_ROWS = (readings.rows as readonly SurveyedRow[]).filter(
  (row) => row.id === 'oudekerk-plein-zuid'
);
const TREE_ROWS = readings.trees as readonly TreeRow[];

/* The yard ends this far behind the row's back walls, so that no wall is read as standing on it. */
const BEHIND_ROW_M = 0.2;
/* Longer than any lane off the Warmoesstraat: the row's back line, drawn out to meet both lanes. */
const BACK_LINE_REACH_M = 200;

function centreline(layout: Layout, id: string): readonly Vec2[] | undefined {
  return layout.streets.find((s) => s.id === id)?.centerline;
}

/* The line along the back walls of the Warmoesstraat row, drawn out both ways from beside `near`. */
function backLine(layout: Layout, near: Vec2): { readonly a: Vec2; readonly b: Vec2 } | null {
  const warmoes = layout.streets.find((s) => s.id === OUDEKERK_TOWER_ROWS[0]!.source);
  if (!warmoes) return null;
  const { point, tangent } = closestPointOnPolyline(warmoes.centerline, near);
  const back = warmoes.width / 2 + LOT.sourceOffset + OUDEKERK_TOWER_ROWS[0]!.depth + BEHIND_ROW_M;
  const side = Math.sign(tangent[1] * (near[0] - point[0]) - tangent[0] * (near[1] - point[1]));
  const on: Vec2 = [point[0] + side * tangent[1] * back, point[1] - side * tangent[0] * back];
  return {
    a: [on[0] - tangent[0] * BACK_LINE_REACH_M, on[1] - tangent[1] * BACK_LINE_REACH_M],
    b: [on[0] + tangent[0] * BACK_LINE_REACH_M, on[1] + tangent[1] * BACK_LINE_REACH_M],
  };
}

/* A lane from the plein to the Warmoesstraat, as far as the row's back line: the plein's end first. */
function laneToRow(
  lane: readonly Vec2[],
  back: { readonly a: Vec2; readonly b: Vec2 }
): readonly Vec2[] | null {
  for (let i = 1; i < lane.length; i++) {
    const crossing = segmentIntersection({ a: lane[i - 1]!, b: lane[i]! }, back);
    if (crossing) return [...lane.slice(0, i), crossing.point];
  }
  return null;
}

/**
 * The yard the 1544 woodcut shows the church standing free in: from the quay south of the choir
 * round the tower to the Enge Kerksteeg, where the back walls of the Warmoesstraat row close it
 * between the two Kerkstegen. West and south-west of the tower the woodcut is hidden by the tower
 * itself, so there the yard ends at the plein's own line and the houses across it are left alone.
 * It is closed through the church, whose own ground is reserved already.
 */
export function oudekerkYard(layout: Layout): Ring | null {
  const kerk = layout.landmarks.find((l) => l.id === 'landmark-oude-kerk')?.footprint;
  const plein = centreline(layout, 'street-oudekerksplein'),
    wijde = centreline(layout, 'street-wijde-kerksteeg'),
    enge = centreline(layout, 'street-enge-kerksteeg'),
    quay = centreline(layout, 'quay-oudezijds-voorburgwal-2');
  if (!kerk || !plein || !wijde || !enge || !quay) return null;
  const back = backLine(layout, wijde[0]!);
  const toWijde = back && laneToRow(wijde, back),
    toEnge = back && laneToRow(enge, back);
  if (!toWijde || !toEnge) return null;
  const atQuay = kerk.reduce((nearest, p) =>
    closestPointOnPolyline(quay, p).distance < closestPointOnPolyline(quay, nearest).distance
      ? p
      : nearest
  );
  const beforeWijde = closestPointOnPolyline(plein, wijde[0]!).segmentIndex + 1;
  return [
    atQuay,
    ...plein.slice(0, beforeWijde),
    ...toWijde,
    ...[...toEnge].reverse(),
    centroid(kerk),
  ];
}

/**
 * The trees the woodcut draws in the yard, read by hand: each row stands `inside` the plein's
 * centreline on the church's side, evenly spaced between its two chainages.
 */
export function oudekerkYardTrees(
  layout: Layout
): readonly { readonly id: string; readonly trees: readonly Vec2[] }[] {
  const kerk = layout.landmarks.find((l) => l.id === 'landmark-oude-kerk')?.footprint;
  if (!kerk) return [];
  const hub = centroid(kerk);
  return TREE_ROWS.flatMap((row) => {
    const street = layout.streets.find((s) => s.id === row.street);
    if (!street) return [];
    const line = measureLine(
      street.centerline,
      street.centerline.map(() => street.width)
    );
    const first = line.pointAt(row.from),
      last = line.pointAt(row.to);
    /* Set off from the two ends alone, so a row along a bend of the plein still stands straight. */
    const ends = [first, last].map(({ point, tangent }): Vec2 => {
      const side = Math.sign(tangent[1] * (hub[0] - point[0]) - tangent[0] * (hub[1] - point[1]));
      return [point[0] + side * tangent[1] * row.inside, point[1] - side * tangent[0] * row.inside];
    });
    const trees = Array.from({ length: row.count }, (_, i): Vec2 => {
      const t = i / (row.count - 1);
      return [
        ends[0]![0] + (ends[1]![0] - ends[0]![0]) * t,
        ends[0]![1] + (ends[1]![1] - ends[0]![1]) * t,
      ];
    });
    return [{ id: row.id, trees }];
  });
}

/** The yard kept clear, and the one row the woodcut draws behind the tower on the Warmoesstraat. */
export function planOudeKerkYard(
  ctx: Pick<BuildContext, 'layout' | 'site'>,
  buildable: PolygonIndex,
  reserved: readonly ReservedFootprint[]
) {
  const ring = oudekerkYard(ctx.layout);
  if (!ring) return { lots: [], reserved: [], unresolved: [] };
  const yard: ReservedFootprint = {
    id: 'surveyed-oudekerk-yard',
    polygon: { outer: ring, holes: [] },
  };
  const lots = surveyedRowLots(
    OUDEKERK_TOWER_ROWS,
    ctx.layout,
    buildable,
    [...reserved, yard],
    57_000
  );
  const built = new Set(lots.map((l) => l.reconstruction!.id));
  const unresolved = OUDEKERK_TOWER_ROWS.flatMap((r) =>
    r.houses.filter((h) => !built.has(h.id)).map((h) => h.id)
  );
  const footprints = lots.map((l): ReservedFootprint => ({
    id: `surveyed:${l.reconstruction!.id}`,
    polygon: { outer: [...l.corners], holes: [] },
  }));
  return { lots, reserved: [yard, ...footprints], unresolved };
}

/*
 * The church-facing row takes priority so estimated canal backs and generic fill
 * cannot occupy its recorded frontage.
 */
export function planOudeKerkPleinFronts(
  ctx: Pick<BuildContext, 'layout' | 'site'>,
  buildable: PolygonIndex,
  reserved: readonly ReservedFootprint[]
) {
  const yard = oudekerkYard(ctx.layout);
  if (!yard) return { lots: [], reserved: [], unresolved: [] };
  const lots = surveyedRowLots(
    OUDEKERK_PLEIN_ROWS,
    ctx.layout,
    buildable,
    [...reserved, { id: 'surveyed-oudekerk-yard', polygon: { outer: yard, holes: [] } }],
    58_000
  );
  const built = new Set(lots.map((lot) => lot.reconstruction!.id));
  return {
    lots,
    reserved: lots.map((lot): ReservedFootprint => ({
      id: `surveyed:${lot.reconstruction!.id}`,
      polygon: { outer: [...lot.corners], holes: [] },
    })),
    unresolved: OUDEKERK_PLEIN_ROWS.flatMap((row) =>
      row.houses.filter((house) => !built.has(house.id)).map((house) => house.id)
    ),
  };
}
